/**
 * Sets a cookie.
 * @param {string} name - Name for the cookie.
 * @param {string} value - Value for the cookie.
 * @param {number} days - Number of days until the cookie should expire.
 */
function setCookie(name, value, days) {
  let expires = '';

  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    expires = `; expires=${date.toUTCString()}`;
  }

  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}${expires}; path=/; SameSite=None; Secure`;
}

/**
 * Gets the value of a cookie (if it exists).
 * @param {string} name - Name of the cookie.
 * @returns {?string}
 */
function getCookie(name) {
  const cookieString = `; ${document.cookie}`;
  const cookies = cookieString.split(`; ${name}=`);

  if (cookies.length === 2) {
    return cookies.pop().split(';').shift();
  }

  return null;
}

/* global Modal */

if (!customElements.get('pop-up')) {
  customElements.whenDefined('modal-dialog').then(() => {
    class PopUp extends Modal {
      constructor() {
        super();
        this.cookie = `${this.id}-dismissed`;
        this.newsletterSubmitKey = 'dewbu-newsletter-popup-submit';
        this.newsletterForm = this.querySelector('form[action*="/contact"]');

        if (this.newsletterForm) {
          this.newsletterForm.addEventListener('submit', this.handleNewsletterSubmit.bind(this));
        }

        const url = new URL(window.location.href);
        const newsletterSubmit = this.getNewsletterSubmit();
        const submittedFromThisPopup = url.searchParams.get('newsletter_popup') === this.id
          || (newsletterSubmit
            && newsletterSubmit.popupId === this.id
            && Date.now() - newsletterSubmit.timestamp < 300000);
        const newsletterSucceeded = url.searchParams.get('customer_posted') === 'true'
          || document.querySelector('.footer-email-signup .alert') !== null
          || this.querySelector('.alert') !== null;
        const newsletterFailed = this.querySelector('.form__field-error') !== null;

        if (Shopify.designMode) {
          document.addEventListener('shopify:section:select', (evt) => {
            if (evt.target === this.closest('.shopify-section')) this.open();
          });

          document.addEventListener('shopify:section:deselect', this.close.bind(this));
        } else if (submittedFromThisPopup && (newsletterSucceeded || newsletterFailed)) {
          if (newsletterSucceeded) {
            this.showNewsletterSuccess();
            document.querySelectorAll('.footer-email-signup .alert').forEach((alert) => {
              alert.hidden = true;
            });
          }

          this.clearNewsletterSubmit();
          this.cleanNewsletterReturnUrl();
          this.open();
        } else if (!getCookie(this.cookie) && (this.dataset.showOnMobile === 'true'
          || theme.mediaMatches.md)) {
          if (this.dataset.trigger === 'delay') {
            this.delayTimer = setTimeout(() => this.open(), Number(this.dataset.delay) * 1000);
          } else if (this.dataset.trigger === 'exit' && theme.mediaMatches.md) {
            this.mouseLeaveHandler = this.mouseLeaveHandler || this.handleMouseLeave.bind(this);
            document.body.addEventListener('mouseleave', this.mouseLeaveHandler);
          } else if (this.dataset.trigger === 'clipboard') {
            this.copyHandler = this.copyHandler || this.handleCopy.bind(this);
            document.addEventListener('copy', this.copyHandler);
          }
        }
      }

      /**
       * Triggers when the web component is removed from the DOM.
       */
      disconnectedCallback() {
        if (this.delayTimer) {
          clearTimeout(this.delayTimer);
        }

        if (this.mouseLeaveHandler) {
          document.body.removeEventListener('mouseleave', this.mouseLeaveHandler);
        }

        if (this.copyHandler) {
          document.removeEventListener('copy', this.copyHandler);
        }
      }

      /**
       * Records that the newsletter form was submitted from this popup and
       * returns to the current URL with an explicit success marker.
       */
      handleNewsletterSubmit() {
        try {
          sessionStorage.setItem(this.newsletterSubmitKey, JSON.stringify({
            popupId: this.id,
            timestamp: Date.now()
          }));
        } catch (error) {
          // Continue with the native Shopify form submission if storage is unavailable.
        }

        let returnToInput = this.newsletterForm.querySelector('input[name="return_to"]');
        if (!returnToInput) {
          returnToInput = document.createElement('input');
          returnToInput.type = 'hidden';
          returnToInput.name = 'return_to';
          this.newsletterForm.appendChild(returnToInput);
        }

        const returnUrl = new URL(window.location.href);
        returnUrl.searchParams.set('customer_posted', 'true');
        returnUrl.searchParams.set('newsletter_popup', this.id);
        returnUrl.hash = '';
        returnToInput.value = `${returnUrl.pathname}${returnUrl.search}`;
      }

      /**
       * Gets the recent popup newsletter submission marker.
       * @returns {?object}
       */
      getNewsletterSubmit() {
        try {
          const storedSubmit = sessionStorage.getItem(this.newsletterSubmitKey);
          return storedSubmit ? JSON.parse(storedSubmit) : null;
        } catch (error) {
          return null;
        }
      }

      /**
       * Clears the popup newsletter submission marker.
       */
      clearNewsletterSubmit() {
        try {
          sessionStorage.removeItem(this.newsletterSubmitKey);
        } catch (error) {
          // No action is required if storage is unavailable.
        }
      }

      /**
       * Ensures the native Shopify success message is shown inside the popup.
       */
      showNewsletterSuccess() {
        if (!this.newsletterForm || this.newsletterForm.querySelector('.alert')) return;

        const successAlert = document.createElement('div');
        successAlert.className = 'alert bg-success-bg text-success-text';
        successAlert.id = `${this.newsletterForm.id}-success`;
        successAlert.tabIndex = -1;
        successAlert.textContent = this.dataset.newsletterSuccess;
        this.newsletterForm.appendChild(successAlert);

        const emailInput = this.newsletterForm.querySelector('input[type="email"]');
        if (emailInput) emailInput.setAttribute('aria-describedby', successAlert.id);
      }

      /**
       * Removes the one-time newsletter return marker without reloading.
       */
      cleanNewsletterReturnUrl() {
        const url = new URL(window.location.href);
        url.searchParams.delete('customer_posted');
        url.searchParams.delete('newsletter_popup');
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      }

      /**
       * Opens the popup and cancels a pending delayed auto-open.
       * @param {Element} opener - Modal opener element.
       */
      open(opener) {
        if (this.delayTimer) {
          clearTimeout(this.delayTimer);
          this.delayTimer = null;
        }

        super.open(opener);

        const successAlert = this.querySelector('.alert');
        if (successAlert) requestAnimationFrame(() => successAlert.focus());
      }

      /**
       * Handles 'mouseleave' events on the body.
       * @param {object} evt - Event object.
       */
      handleMouseLeave(evt) {
        if (evt.clientY < 0 && !getCookie(this.cookie)) this.open();
      }

      /**
       * Handles 'copy' events on the body.
       */
      handleCopy() {
        if (!getCookie(this.cookie)) this.open();
      }

      /**
       * Handles 'close' events on the modal.
       */
      close() {
        super.close();
        setCookie(this.cookie, true, this.dataset.dismissDays);
      }
    }

    customElements.define('pop-up', PopUp);
  });
}
